import React from 'react';
import { formatDate, getCategoryName, getCategoryBadgeClass } from '../utils/helpers';
import { ClockIcon } from './Icons';

const FeaturedArticleCard = ({ 
  article, 
  isLarge = false,
  isMain = false,
  compact = false,
  priority = false,
  className = '',
  showCategory = true
}) => {
  if (!article) return null;
  
  const { 
    id,
    title, 
    slug, 
    category,
    image,
    imageUrl,
    description,
    excerpt,
    date,
    createTime,
    readTime,
    duration
  } = article;
  
  const displayImage = imageUrl || image || '/placeholder-image.jpg';
  const categoryName = getCategoryName(category);
  const categoryBadge = getCategoryBadgeClass(categoryName);
  const displayDescription = description || excerpt || '';
  const displayDate = formatDate(createTime || date) || 'Recent';
  const displayReadTime = (readTime || duration || '5 min').includes('min') 
    ? (readTime || duration) 
    : `${readTime || duration || '5'} min read`;
  
  // Compact version for carousel
  if (compact) {
    return (
      <div className={`bg-white rounded-xl overflow-hidden shadow-sm h-full flex flex-col ${className}`}>
        <a href={`/article.html?id=${id || slug}`} className="block group h-full flex flex-col">
          <div className="relative aspect-[16/9] overflow-hidden">
            <img
              src={displayImage}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading={priority ? 'eager' : 'lazy'}
            />
            {showCategory && categoryName && (
              <div className="absolute top-3 left-3">
                <span className={`inline-block px-3 py-1 text-xs font-medium border ${categoryBadge} rounded-full shadow-sm`}>
                  {categoryName}
                </span>
              </div>
            )}
          </div>
          
          <div className="p-4 flex-1 flex flex-col">
            <h2 className="text-lg font-bold text-stone-900 mb-2 line-clamp-2 group-hover:text-slate-600 transition-colors">
              {title}
            </h2>
            
            {displayDescription && (
              <p className="text-stone-600 text-sm mb-4 line-clamp-2 flex-1">
                {displayDescription}
              </p>
            )}
            
            <div className="text-xs text-stone-500 flex items-center">
              <span>{displayDate}</span>
              <span className="mx-2">•</span>
              <div className="flex items-center">
                <ClockIcon className="w-3 h-3 mr-1" />
                <span>{displayReadTime}</span>
              </div>
            </div>
          </div>
        </a>
      </div>
    );
  }
  
  // Large featured card
  if (isLarge || isMain) {
    return (
      <div className={`featured-article-card-large rounded-xl overflow-hidden shadow-md ${isMain ? 'lg:col-span-2 lg:row-span-2' : ''} ${className}`}>
        <a href={`/article.html?id=${id || slug}`} className="block group h-full">
          <div className="relative h-full flex flex-col overflow-hidden min-h-[400px]">
            <div className="relative w-full h-full overflow-hidden bg-stone-100">
              <img
                src={displayImage}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading={priority ? 'eager' : 'lazy'}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
            </div>
            
            <div className="absolute inset-0 flex flex-col h-full justify-end p-6 text-white">
              {showCategory && categoryName && (
                <div className="mb-4">
                  <span className={`inline-block px-3 py-1.5 text-sm font-medium border ${categoryBadge} rounded-full shadow-lg`}>
                    {categoryName}
                  </span>
                </div>
              )}
              
              <h2 className="font-bold text-white text-xl md:text-2xl leading-tight mb-3 line-clamp-2 group-hover:text-slate-300 transition-colors"
                  style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                {title}
              </h2>
              
              {displayDescription && (
                <p className="text-white/90 text-sm md:text-base mb-4 line-clamp-2"
                   style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                  {displayDescription}
                </p>
              )}
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/80" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                  <span>{displayDate}</span>
                  <span className="mx-2">•</span>
                  <span>{displayReadTime}</span>
                </div>
                
                <span className="inline-flex items-center px-4 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-sm font-medium shadow-lg transition-all duration-300 group-hover:translate-x-1">
                  Read More →
                </span>
              </div>
            </div>
          </div>
        </a>
      </div>
    );
  }
  
  // Standard featured card
  return (
    <div className={`bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col ${className}`}>
      <a href={`/article.html?id=${id || slug}`} className="block group h-full flex flex-col">
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={displayImage}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading={priority ? 'eager' : 'lazy'}
          />
          
          {showCategory && categoryName && (
            <div className="absolute top-3 left-3">
              <span className={`inline-block px-3 py-1 text-xs font-medium border ${categoryBadge} rounded-full shadow-sm`}>
                {categoryName}
              </span>
            </div>
          )}
        </div>
        
        <div className="p-4 flex-1 flex flex-col">
          <h2 className="text-lg font-bold text-stone-900 mb-2 line-clamp-2 group-hover:text-slate-600 transition-colors">
            {title}
          </h2>
          
          {displayDescription && (
            <p className="text-stone-600 text-sm mb-4 line-clamp-2 flex-1">
              {displayDescription}
            </p>
          )}
          
          <div className="text-xs text-stone-500 flex items-center">
            <span>{displayDate}</span>
            <span className="mx-2">•</span>
            <div className="flex items-center">
              <ClockIcon className="w-3 h-3 mr-1" />
              <span>{displayReadTime}</span>
            </div>
          </div>
        </div>
      </a>
    </div>
  );
};

export default FeaturedArticleCard; 